import { Box, Chip, CircularProgress, Link as MuiLink, Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { Link as RouterLink, useSearchParams } from "react-router-dom";

import { PageHeader } from "../../components/ui/page-header.js";
import { listAllDocs } from "./api.js";
import { DocSearchBox } from "./DocSearchBox.js";
import { searchDocs } from "./docSearch.js";
import { StatusChip } from "./type-release/rowParts.js";

/** Free-text search across the WHOLE Documentation catalogue — every category
 * and every release at once. Reuses the sidebar's complete-catalogue query
 * (`["docs","tree","all"]`) so it shares the cache and needs no extra fetch,
 * then filters client-side via the pure `searchDocs`. The query lives in the
 * URL (`?q=`), so results are bookmarkable / shareable and the sidebar +
 * page inputs stay in lockstep. */
export function DocumentationSearchPage(): JSX.Element {
  const [params] = useSearchParams();
  const q = params.get("q") ?? "";

  // Same key + queryFn as DocumentationSidebar — one cached pull of the whole
  // library backs both the tree and search.
  const { data: allDocs, isLoading } = useQuery({
    queryKey: ["docs", "tree", "all"],
    queryFn: () => listAllDocs({ q: "", category_id: "all" }),
  });

  const results = useMemo(() => searchDocs(allDocs ?? [], q), [allDocs, q]);
  const trimmed = q.trim();

  return (
    <Box sx={{ display: "grid", gap: 2.5 }}>
      <PageHeader
        title="Search the documentation"
        description="Find any file across every category and release."
      />

      <DocSearchBox autoFocus />

      {!trimmed ? (
        <Box
          data-testid="doc-search-prompt"
          sx={{ borderRadius: 1, border: 1, borderColor: "divider", p: 4, textAlign: "center", color: "text.secondary" }}
        >
          <Typography sx={{ fontSize: 14 }}>
            Start typing to search across every category and release — by title,
            summary, category, release, tag, or document type.
          </Typography>
        </Box>
      ) : isLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress size={28} />
        </Box>
      ) : results.length === 0 ? (
        <Box
          data-testid="doc-search-empty"
          sx={{ borderRadius: 1, border: 1, borderColor: "divider", p: 4, textAlign: "center", color: "text.secondary" }}
        >
          <Typography sx={{ fontSize: 14 }}>
            No documents match <strong>“{trimmed}”</strong>. Try fewer or
            different words.
          </Typography>
        </Box>
      ) : (
        <>
          <Typography data-testid="doc-search-count" sx={{ fontSize: 13, color: "text.secondary" }}>
            {results.length} result{results.length === 1 ? "" : "s"} for <strong>“{trimmed}”</strong>
          </Typography>
          <Box component="ul" data-testid="doc-search-results" sx={{ listStyle: "none", m: 0, p: 0, display: "grid", gap: 1 }}>
            {results.map((doc) => (
              <Box
                key={doc.id}
                component="li"
                data-testid={`doc-search-row-${doc.id}`}
                sx={{ borderRadius: 1, border: 1, borderColor: "divider", p: 1.5, "&:hover": { bgcolor: "action.hover" } }}
              >
                <MuiLink
                  component={RouterLink}
                  to={`/documentation/library/${doc.slug}`}
                  underline="hover"
                  sx={{ fontSize: 14, fontWeight: 600, color: "text.primary" }}
                >
                  {doc.title}
                </MuiLink>
                {doc.summary && (
                  <Typography
                    sx={{ mt: 0.25, fontSize: 13, color: "text.secondary", overflow: "hidden", textOverflow: "ellipsis", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" }}
                  >
                    {doc.summary}
                  </Typography>
                )}
                <Box sx={{ mt: 0.75, display: "flex", flexWrap: "wrap", gap: 0.5, alignItems: "center" }}>
                  <Chip
                    size="small"
                    label={doc.category?.name ?? "Uncategorized"}
                    sx={{ height: 20, borderRadius: 0.75, fontSize: 11, bgcolor: "surfaceSoft", color: "text.secondary" }}
                  />
                  <Chip
                    size="small"
                    label={doc.default_release ? `v${doc.default_release.version}` : "Unversioned"}
                    sx={{ height: 20, borderRadius: 0.75, fontSize: 11, bgcolor: "surfaceSoft", color: "text.secondary" }}
                  />
                  <Chip
                    size="small"
                    label={doc.doc_type}
                    sx={{ height: 20, borderRadius: 0.75, fontSize: 11, bgcolor: "surfaceSoft", color: "text.secondary", textTransform: "capitalize" }}
                  />
                  <StatusChip tone={doc.status === "published" ? "good" : "neutral"}>{doc.status}</StatusChip>
                </Box>
              </Box>
            ))}
          </Box>
        </>
      )}
    </Box>
  );
}
